import React, { useState, useEffect } from "react";
import { Modal, Form, AutoComplete, Select, Radio, Switch, message } from "antd";
import { connectorAttributeMappings, klaviyoPicklists, useBlustreamApiQuery } from "../../../apiService/blustreamApi";
import PicklistLoadState from "./PicklistLoadState";

const { Option } = Select;

export default function AttributeMappingModal({ visible, onClose, onSaved, editMapping, attributeNames = {} }) {
  const [form] = Form.useForm();
  const [direction, setDirection] = useState("OUTBOUND");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profilePropertiesRetryKey, setProfilePropertiesRetryKey] = useState(0);
  const isEdit = !!editMapping;

  // Sampled, never authoritative -- Klaviyo has no endpoint enumerating custom
  // profile properties, so a client's real property can legitimately be
  // absent here. Fetched only while the modal is open; retry re-runs the same
  // fetch without touching any other state.
  const { data: profileProperties, error: profilePropertiesError } = useBlustreamApiQuery(
    (signal) => (visible ? klaviyoPicklists.getProfileProperties({ signal }) : Promise.resolve(null)),
    [visible, profilePropertiesRetryKey]
  );
  const profilePropertyOptions = (profileProperties || []).map((p) => ({ value: p }));

  useEffect(() => {
    if (visible) {
      if (editMapping) {
        form.setFieldsValue({
          dataClassAttributeId: editMapping.data_class_attribute_id,
          vendorField: editMapping.vendor_field || "",
        });
        setDirection(editMapping.direction || "OUTBOUND");
        setEnabled(editMapping.enabled !== false);
      } else {
        form.resetFields();
        setDirection("OUTBOUND");
        setEnabled(true);
      }
    }
  }, [visible, editMapping, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const body = {
        data_class_attribute_id: values.dataClassAttributeId,
        direction,
        vendor_field: values.vendorField,
        enabled,
      };

      if (isEdit) {
        await connectorAttributeMappings.update(editMapping.attribute_mapping_id, body);
        message.success("Attribute mapping updated");
      } else {
        await connectorAttributeMappings.create(body);
        message.success("Attribute mapping created");
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to save attribute mapping");
    }
    setSaving(false);
  };

  // Build display label for existing mapping attribute that may not be in
  // attributeNames. The mapping API no longer returns a name for the
  // attribute, so this resolves it from the id -> name map the parent tab
  // builds from /api/beta/attributes; falls back to a generic label (never
  // blank) when a name is genuinely unavailable.
  const getAttributeLabel = () => {
    if (!editMapping) return null;
    const name = attributeNames[editMapping.data_class_attribute_id];
    return name
      ? `${name} (${editMapping.data_class_attribute_id})`
      : `Attribute #${editMapping.data_class_attribute_id}`;
  };

  // Sorted by name so the dropdown is actually usable once it has more than
  // a handful of attributes in it.
  const attributeOptions = Object.entries(attributeNames)
    .map(([id, name]) => ({ id: Number(id), name }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <Modal
      title={isEdit ? "Edit Attribute Mapping" : "Add Attribute Mapping"}
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      cancelText="Cancel"
      width={520}
      confirmLoading={saving}
      okButtonProps={{ className: "standard-button primary-orange-button" }}
      cancelButtonProps={{ className: "standard-button secondary-orange-button" }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          label="Polly Attribute"
          name="dataClassAttributeId"
          rules={[{ required: true, message: "Select an attribute" }]}
        >
          <Select
            placeholder="Select attribute"
            className="standard-input"
            showSearch
            optionFilterProp="children"
            disabled={isEdit}
          >
            {/* Show the existing attribute if editing and it's not in attributeNames */}
            {isEdit && editMapping?.data_class_attribute_id != null &&
              !(editMapping.data_class_attribute_id in attributeNames) && (
                <Option
                  key={editMapping.data_class_attribute_id}
                  value={editMapping.data_class_attribute_id}
                >
                  {getAttributeLabel()}
                </Option>
              )}
            {attributeOptions.map((opt) => (
              <Option key={opt.id} value={opt.id}>
                {opt.name} ({opt.id})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Direction">
          <Radio.Group
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
          >
            <Radio.Button value="OUTBOUND">Outbound</Radio.Button>
            <Radio.Button value="INBOUND">Inbound</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Vendor Field"
          name="vendorField"
          rules={[{ required: true, message: "Enter the Klaviyo profile property name" }]}
        >
          <AutoComplete
            id="vendorField"
            className="standard-input"
            placeholder="e.g., watch_color"
            options={profilePropertyOptions}
            // Force the dropdown closed when there's nothing to suggest --
            // rc-select would otherwise render an empty "no data" panel,
            // which reads as "Klaviyo has no properties" rather than "we
            // don't have a sample yet". The field itself stays a normal
            // editable input either way, so free text always still submits.
            open={profilePropertyOptions.length > 0 ? undefined : false}
            filterOption={(inputValue, option) =>
              option.value.toLowerCase().includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
        {/* A Klaviyo outage must not block saving a mapping whose property
            name the client already knows, so this renders beside the field
            rather than replacing it. */}
        <PicklistLoadState
          error={profilePropertiesError}
          onRetry={() => setProfilePropertiesRetryKey((k) => k + 1)}
        />
        {!profilePropertiesError && profileProperties && profileProperties.length === 0 && (
          <div className="field-note-text" style={{ fontSize: 12, color: "#888", marginTop: -8, marginBottom: 12 }}>
            No sampled property suggestions yet -- you can still type the property name directly.
          </div>
        )}

        <Form.Item label="Enabled">
          <Switch checked={enabled} onChange={setEnabled} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
